import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { ChecklistOption } from './enrollmentService';

export interface SubscriberPage {
  result: Subscriber[];
  totalElements: number;
  currentPage: number;
  totalPages: number;
}

export interface Subscriber {
  id: string;
  /**
   * @deprecated Use `periodId` from the page URL instead.
   * This field will be removed in the future and currently returns null.
   */
  periodId: string;
  groupId: string;
  ssn: string;
  email?: string;
  phone?: string;
  employeeClass: string;
  selfEnrollStatus: EmployeeInvitationStatusTypes | null;
  subSERole: SubscriberSERoles;
  seInviteDate: string | null;
  productRates?: RateTiers;
  userId: string | null;
  choice: Choice[];
  common: PersonInfo;
  custom: CustomInfo;
  productLineToCarrier: ProductLineToCarrier;
  electionStatus: ElectionStatus;
  history: HistoryEvent[];
  addOnDate: string;
  addOnMethod: SubscriberAddOnMethod;
  progress: SubscriberProgress;
  tasks: SubscriberTask[];
  interestedProductLines: InterestedProductLine[];
}

export interface InterestedProductLine {
  productLineId: ProductLineId;
  dependentIds: string[];
  value: ProductLineInterestSelection;
}

export interface InterestedProductLineInput {
  productLineId: ProductLineId;
  dependentIds: string[];
  value: string;
}

/**
 * Subscriber data structure.
 * Contains all information needed to render and interact with a subscriber card.
 */
export interface SubscriberPresenter {
  id: string;
  common: PersonInfo;
  choice: Choice[];
  employeeClass: string;
  productLineToCarrier: ProductLineToCarrier;
  custom: CustomInfo;
  interestedProductLines: InterestedProductLine[];
}

/**
 * Person information (subscriber or dependent).
 * Contains display fields (firstName, lastName) and any additional information.
 */
export interface PersonInfo {
  [key: string]: string;
  firstName: string;
  lastName: string;
}

/**
 * Custom subscriber information.
 * Contains additional data specific to the subscriber (e.g., dependents list).
 */
export interface CustomInfo {
  dependents: PersonInfo[];
}

/**
 * Product line choice for a subscriber.
 * Represents the selected/waived/not selected product and list of selected dependent ids.
 */
export interface Choice {
  productLineId: ProductLineId | null;
  productId: string;
  productName: string;
  dependentIds: string[];
}

/**
 * Default/initial choice state for a product line.
 * Used when no choice has been made yet.
 */
export const INITIAL_CHOICE = {
  productLineId: null,
  productId: '',
  productName: '',
  dependentIds: [],
};

export const INITIAL_INTERESTED_PRODUCT_LINE = {
  productLineId: null,
  value: '' as ProductLineInterestSelection,
  dependentIds: [],
};
/**
 * Updated plan/product selection information.
 * Contains the subscriber and product line context for a plan change.
 */
export interface UpdatedPlanInfo {
  subscriberId: string;
  selectedPlanId: string;
  selectedPlanName: string;
  productLineId: ProductLineId;
}

/**
 * Selection for a subscriber interest product line.
 * Represents the selection of a product line ('I' = interested, 'null' = waived (not interested), '' = not selected).
 */
export type ProductLineInterestSelection = 'I' | 'null' | '';

/**
 * Change in product line interest selection.
 * Represents the change in the selection of a product line (interested, waived, or unselected).
 */
export interface ProductLineInterestChange {
  subscriberId: string;
  productLineId: ProductLineId;
  selection: ProductLineInterestSelection;
}

/**
 * Dependents selection for a product line.
 * Represents which dependents are selected for a specific product line.
 */
export interface DependentsChoice {
  subscriberId: string;
  productLineId: ProductLineId;
  dependentIds: string[];
}

/**
 * Product information for a product line.
 */
export interface ProductInfo {
  productLineId: ProductLineId;
  portfolioId: string;
  plans: PlanDetails[];
}

/**
 * Details of a specific plan/product option.
 * Contains plan identification, name, carrier, and eligible employee classes.
 */
export interface PlanDetails {
  id: string;
  name: string;
  carrierId: string;
  eligibleClasses: string[];
}

/**
 * Types of employee classes.
 * Defines the classification system used for employee classes.
 */
export type EeClassesTypes = 'LOCATION' | 'STOCK' | 'CUSTOM';

export interface EeClass {
  id: string;
  name: string;
  type: EeClassesTypes;
  periodId: string;
  groupId: string;
}

/**
 * Employee class information with validation.
 * Represents a class option available for selection in the subscriber card.
 */
export interface EeClassWithValidation extends EeClass {
  validationMessage?: string;
}

/**
 * Mapping of product line IDs to the carrier id.
 * Used to determine carrier-specific validation requirements.
 */
export type ProductLineToCarrier = {
  [key in ProductLineId]?: string;
};

/**
 * Server-provided rules for which subscriber and product-line data must be collected, and which dynamic forms
 * back those fields. This is the raw API shape: it carries **both** enrollment and quoting form ids/field lists
 * so one response can serve multiple flows.
 * UI layers pick the slice they need via {@link createRequiredSubInfoPresenter} into {@link RequiredSubInfoPresenter}.
 */
export interface RequiredSubInfo {
  /**
   * Mapping of product line IDs to additional required fields for the enrollment flow.
   * Each product line can have its own required fields for elected/waived states and dependency relationships.
   */
  productLineRequiredInfo?: Partial<Record<ProductLineId, ProductLineAdditionalFields>>;
  manageDependentFormId: string;
  // Default enrollment flow fields
  editSubFormId: string;
  /** Keys on `subscriber.common` treated as required for profile completeness in the **enrollment** flow. */
  requiredFields: string[];

  // Initial quoting flow fields
  quotingSubFormId: string;
  /** Keys on `subscriber.common` required for profile completeness in the **quoting** flow. */
  quotingRequiredFields: string[];
  quotingProductLineIds: string[];
}

/**
 * Selects which half of `RequiredSubInfo` feeds the card and validators: enrollment (`editSubFormId` +
 * `requiredFields`) versus quoting (`quotingSubFormId` + `quotingRequiredFields`).
 */
export type ElectionFlow = 'enrollment' | 'quoting';

/**
 * Unified view of required subscriber rules used by the Subscriber Card and helpers.
 *
 * **Flow:** Build this with `createRequiredSubInfoPresenter` (`requiredSubInfo`, `flow`) where `flow` is {@link ElectionFlow}.
 * That is the only place `ElectionFlow` should branch — `productLineRequiredInfo` is shared;
 * subscriber-level `subscriberRequiredFields` / `subscriberFormId` are flow-specific.
 * Validators (`checkTotalRateCalculation`, `getProductLineRequiredFields`, etc.) take this presenter
 * and stay flow-agnostic.
 */
export interface RequiredSubInfoPresenter {
  productLineRequiredInfo?: RequiredSubInfo['productLineRequiredInfo'];
  dependentsFormId: string;
  subscriberFormId: string;
  subscriberRequiredFields: string[];
}

/**
 * Additional validation fields for a product line.
 * Defines required fields for elected/waived states and dependency relationships.
 */
export interface ProductLineAdditionalFields {
  elected?: RequiredFields;
  waived?: RequiredFields;
  dependentOn?: ProductLineId;
}

/**
 * Required field configuration for a specific product line action (elected/waived).
 * Defines which fields are required based on employee class, carrier, and product.
 */
export interface RequiredFields {
  formId: string;
  requiredFields: string[];
  classRequiredFields: Record<string, string[]>;
  carrierRequiredFields: Record<string, string[]>;
  productRequiredFields: Record<string, string[]>;
}

/**
 * Types of validation error messages for subscribers.
 * Used to categorize and display different validation errors.
 */
export type SubscriberErrorMessageType =
  | 'INCOMPLETE_PROFILE'
  | 'MISSING_PRODUCT_LINE_ANSWERS'
  | 'MISSING_DEPENDENT_PRODUCT_LINE_SELECTION';

export type RateTiers = {
  [key in ProductLineId]: ProductLineRateInfo;
};

export type ProductLineRateInfo = {
  [key: string]: RateCount;
};

export interface RateCount {
  [key: string]: number;
}

export interface ProductLineSubRate {
  productLineId: ProductLineId;
  rate: string;
}

export enum SubscriberTaskStatus {
  READY_TO_BEGIN = 'READY_TO_BEGIN',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CLOSED = 'CLOSED',
}

export interface SubscriberTask {
  name: string;
  description: string;
  entityId: string;
  checklist: TaskChecklist;
  status: SubscriberTaskStatus;
}

export interface TaskChecklist {
  checklistOptions: ChecklistOption<SubChecklistCard>[];
}

export const SUBSCRIBER_TYPES: Record<string, string> = {
  P: 'Primary',
  S: 'Spouse',
  D: 'Child',
  DP: 'Domestic Partner',
};

export enum SubscriberAddOnMethod {
  CENSUS = 'CENSUS',
  LINK = 'LINK',
  FORM = 'FORM',
}

export enum SubscriberProgress {
  NOT_YET_STARTED = 'NOT_YET_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  INCOMPLETE_PROFILE = 'INCOMPLETE_PROFILE',
  PARTIALLY_ELECTED = 'PARTIALLY_ELECTED',
  FINALIZE_ELECTION = 'FINALIZE_ELECTION',
  ELECTION_PENDING = 'ELECTION_PENDING',
  ELECTION_COMPLETED = 'ELECTION_COMPLETED',
}

export type SubscriberIMQProgress =
  | SubscriberProgress.NOT_YET_STARTED
  | SubscriberProgress.IN_PROGRESS
  | SubscriberProgress.COMPLETED;

export type SubscriberAddEmployeesProgress =
  | SubscriberProgress.INCOMPLETE_PROFILE
  | SubscriberProgress.PARTIALLY_ELECTED
  | SubscriberProgress.FINALIZE_ELECTION
  | SubscriberProgress.ELECTION_COMPLETED;

export enum EmployeeInvitationStatusTypes {
  NOT_YET_INVITED = 'NOT_YET_INVITED',
  INVITED = 'INVITED',
  ADD_MAIL = 'ADD_MAIL',
  REGISTERED = 'REGISTERED',
  ELECTED = 'ELECTED',
}

export enum SubscriberSERoles {
  EMPLOYEE = 'EMPLOYEE',
  EMPLOYER = 'EMPLOYER',
}

export type ElectionStatus = 'NEW_HIRE' | 'ENROLLED' | 'UPDATING' | 'PENDING' | 'TERMINATED';

export interface SelectedPlanInProductLine {
  productLineId: ProductLineId;
  selectedPlanId: string;
  selectedPlanName: string;
}

export type SavingStatus = 'SAVING' | 'SUCCESSFUL_SAVING' | 'FAILED_SAVING';

export interface FullStatistics extends StatisticsValues {
  rateStats: RateStatistic;
}

export interface StatisticsValues {
  total: number;
  elected: number;
  electionsMissing: number;
  waived: number;
  invited: number;
  registered: number;
  subRates?: SubRates;
  estimatedCost: EstimatedCostStatistics;
}

interface QuotingProductLineStat {
  productLineId: ProductLineId;
  eesOnly: number;
  eesWithDeps: number;
  depsTotal: number;
}

export interface QuotingStatistics {
  eesOnly: number;
  eesWithDeps: number;
  depsTotal: number;
  productLineStat: QuotingProductLineStat[];
}

/**
 * Premium rate for a product line.
 * Contains the product line ID and its corresponding rate value.
 */
export interface SubRates {
  [key: string]: ProductLineSubRate[];
}

export type EstimatedCostStatistics = {
  [key in ProductLineId]: EstimatedProductLineStatistics;
};

interface EstimatedProductLineStatistics {
  monthlyCost: string;
  annualCost: string;
}

export type RateStatistic = {
  [key in ProductLineId]: RateCount;
};

export interface StatisticCard {
  value: number;
  title: string;
}

export interface StatisticsFields {
  total: StatisticCard;
  elected: StatisticCard;
  electionsMissing: StatisticCard;
  waived: StatisticCard;
  invited: StatisticCard;
  registered: StatisticCard;
}

export enum SubscribersSortBy {
  NAME = 'NAME',
  ELECTED = 'ELECTED',
}

export enum SubscribersSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum SubscribersFilter {
  ALL = 'ALL',
  NOT_YET_INVITED = 'NOT_YET_INVITED',
  INVITED = 'INVITED',
  REGISTERED = 'REGISTERED',
  ELECTED = 'ELECTED',
  NOT_YET_ELECTED = 'NOT_YET_ELECTED',
  NOT_YET_IN_CLASS = 'NOT_YET_IN_CLASS',
  IN_CLASS = 'IN_CLASS',
  MISSING_INFORMATION = 'MISSING_INFORMATION',
}

export interface EnrollsUsers {
  [key: string]: string;
}

export interface ProductsInClass {
  productId: string;
  employeeClasses: string[];
}

export interface CompleteElectionsCheck {
  allWithClasses: boolean;
  subsWithClass: number;
  isProductLinesQuestionAnswered: boolean;
  isDependentOnProductLineValid: boolean;
  isWaiverQuestionAnswered: boolean;
  subsWithoutElection: number;
  generalQuestionAnswered: boolean;
}

export interface SubscribersPerEnrollmentParams {
  groupId: string;
  periodId: string;
  sortBy: SubscribersSortBy;
  sortOrder: SubscribersSortOrder;
  filter: SubscribersFilter;
  status: EmployeeInvitationStatusTypes[];
  employeeClasses: string[];
  progress: SubscriberProgress[];
  page: number;
  name?: string;
}

export interface SubscribersPerGroupParams {
  groupId: string;
  cartId?: string;
  sortBy: SubscribersSortBy;
  sortOrder: SubscribersSortOrder;
  filter: SubscribersFilter;
  status: EmployeeInvitationStatusTypes[];
  addOnMethod: SubscriberAddOnMethod[];
  progress: SubscriberProgress[];
  name?: string;
  page: number;
}

export interface HistoryEvent {
  reason: string;
  date: string;
  actions: string[];
}

export enum SubChecklistCard {
  MANAGE_SUBSCRIBER = 'MANAGE_SUBSCRIBER',
  REVIEW_AND_CONFIRM = 'REVIEW_AND_CONFIRM',
  SUBSCRIBER_PROFILE = 'SUBSCRIBER_PROFILE',
  SUBSCRIBER_DEPENDENTS = 'SUBSCRIBER_DEPENDENTS',
  SUBSCRIBER_DOCUMENTS_IMQ = 'SUBSCRIBER_DOCUMENTS_IMQ',
  QUOTING_SUBSCRIBER_PROFILE = 'QUOTING_SUBSCRIBER_PROFILE',
  QUOTING_ELECTIONS = 'QUOTING_ELECTIONS',
  HEALTH_QUESTIONNAIRE = 'HEALTH_QUESTIONNAIRE',
  IMQ_GENERAL_INFO = 'IMQ_GENERAL_INFO',
  IMQ_SUBSCRIBER_PROFILE = 'IMQ_SUBSCRIBER_PROFILE',
  IMQ_SUBSCRIBER_DEPENDENTS = 'IMQ_SUBSCRIBER_DEPENDENTS',
}

/**
 * Product line state types.
 * Represents whether a product line is selected, waived, or not selected. The product id is used to determine the type.
 */
export type SubscriberProductLineType = 'NOT_SELECTED' | 'SELECTED' | 'WAIVED';
